import React, { useState } from 'react';
import { Crown, Check, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { stripeAPI } from '../../services/api';
import { STRIPE_PLANS, STANDARD_FEATURES } from '../../config/stripe';
import { toast } from 'sonner';

const SUBTEXT = {
  export: 'Export is available on the Standard plan.',
  trial_expired: 'Your 14-day trial has ended.',
  ai: 'AI map completion is available on the Standard plan.',
  upgrade_prompt: 'Unlock the full Lifely experience.',
};

/**
 * Upgrade modal shown when a user hits a paid feature or chooses to upgrade.
 * Props: { isOpen, onClose, trigger: 'export' | 'trial_expired' | 'upgrade_prompt' }
 */
const UpgradeModal = ({ isOpen, onClose, trigger = 'upgrade_prompt' }) => {
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handleCheckout = async (planKey) => {
    setLoadingPlan(planKey);
    try {
      const { url } = await stripeAPI.createCheckoutSession(planKey);
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout failed:', err);
      toast.error('Could not start checkout. Please try again.');
      setLoadingPlan(null);
    }
  };

  const monthly = STRIPE_PLANS.monthly;
  const annual = STRIPE_PLANS.annual;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="upgrade-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Crown className="w-5 h-5 text-primary" />
            Upgrade to Standard
          </DialogTitle>
          <DialogDescription>{SUBTEXT[trigger] || SUBTEXT.upgrade_prompt}</DialogDescription>
        </DialogHeader>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Monthly */}
          <div className="rounded-xl border border-border p-4 flex flex-col">
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-heading font-semibold">{monthly.price}</span>
              <span className="text-sm text-muted-foreground">{monthly.period}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{monthly.caption}</p>
            <Button
              variant="outline"
              className="w-full mt-4"
              onClick={() => handleCheckout('monthly')}
              disabled={loadingPlan !== null}
              data-testid="upgrade-monthly-button"
            >
              {loadingPlan === 'monthly' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Get started
            </Button>
          </div>

          {/* Annual — featured */}
          <div className="relative rounded-xl border-2 border-primary bg-primary/5 p-4 flex flex-col">
            <Badge className="absolute -top-2.5 right-3 bg-primary text-primary-foreground text-[10px]">
              Best value
            </Badge>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-heading font-semibold">{annual.price}</span>
              <span className="text-sm text-muted-foreground">{annual.period}</span>
            </div>
            <p className="text-xs text-primary font-medium mt-1">{annual.caption}</p>
            <Button
              className="w-full mt-4"
              onClick={() => handleCheckout('annual')}
              disabled={loadingPlan !== null}
              data-testid="upgrade-annual-button"
            >
              {loadingPlan === 'annual' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Get started
            </Button>
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-2 pt-1">
          {STANDARD_FEATURES.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <div className="text-center pt-1">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            data-testid="upgrade-maybe-later"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
